import React, { useEffect, useState } from 'react'
import { UserAdmin, UserUpdateAdmin } from '../services/userService'

interface Props {
  isOpen: boolean
  user: UserAdmin | null
  canEditRole: boolean
  onClose: () => void
  onSubmit: (userId: number, payload: UserUpdateAdmin, isAdmin: boolean) => Promise<UserAdmin>
}

export default function EditUserModal({ isOpen, user, canEditRole, onClose, onSubmit }: Props) {
  const [form, setForm] = useState<UserUpdateAdmin>({ username: '', email: '', full_name: '', is_active: true, password: '' })
  const [passwordConfirm, setPasswordConfirm] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>('')
  const [role, setRole] = useState<'admin' | 'user'>('user')

  useEffect(() => {
    if (!isOpen || !user) return
    setForm({ username: user.username, email: user.email, full_name: user.full_name, is_active: user.is_active })
    setSaving(false)
    setPasswordConfirm('')
    setError('')
    const isAdmin = (user.permissions || []).includes('admin') || !!user.is_superuser
    setRole(isAdmin ? 'admin' : 'user')
  }, [isOpen, user])

  if (!isOpen || !user) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (form.password && form.password.trim().length > 0) {
      if (form.password.trim().length < 6) {
        setError('비밀번호는 최소 6자 이상이어야 합니다.')
        return
      }
      if (form.password !== passwordConfirm) {
        setError('비밀번호와 비밀번호 확인이 일치하지 않습니다.')
        return
      }
    }
    try {
      setSaving(true)
      await onSubmit(user.id, form, role === 'admin')
      onClose()
    } catch (e: any) {
      const raw = (e?.message || '').toString()
      if (raw && /password/i.test(raw) && /min|short|6/.test(raw)) {
        setError('비밀번호는 최소 6자 이상이어야 합니다.')
      } else {
        try {
          const parsed = JSON.parse(raw)
          const detail = (parsed && (parsed.detail || parsed.message))
          setError(detail ? String(detail) : '저장에 실패했습니다. 잠시 후 다시 시도해 주세요.')
        } catch {
          setError('저장에 실패했습니다. 잠시 후 다시 시도해 주세요.')
        }
      }
    } finally {
      setSaving(false)
    }
  }

  const roleEditable = canEditRole

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">사용자 수정</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="닫기">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">아이디 (username)</label>
            <input
              className="w-full px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-600"
              value={user.username}
              readOnly
              aria-disabled
            />
            <p className="text-xs text-gray-500 mt-1">아이디는 수정할 수 없습니다.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
              value={form.email || ''}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="예: user@example.com"
              disabled={saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
              value={form.full_name || ''}
              onChange={e => setForm({ ...form, full_name: e.target.value })}
              placeholder="예: 홍길동"
              disabled={saving}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">역할</label>
              {user.is_superuser && (
                <span className="text-xs text-red-600 font-medium">기본 생성된 관리자 계정</span>
              )}
            </div>
            <div className="flex gap-4 mt-1">
              <label className="inline-flex items-center text-sm text-gray-700">
                <input
                  type="radio"
                  className="mr-2"
                  value="user"
                  checked={role === 'user'}
                  onChange={() => setRole('user')}
                  disabled={saving || !roleEditable}
                />
                사용자
              </label>
              <label className="inline-flex items-center text-sm text-gray-700">
                <input
                  type="radio"
                  className="mr-2"
                  value="admin"
                  checked={role === 'admin'}
                  onChange={() => setRole('admin')}
                  disabled={saving || !roleEditable}
                />
                관리자
              </label>
            </div>
            {!roleEditable && (
              <p className="mt-1 text-xs text-gray-500">현재 로그인한 계정은 여기서 역할을 바꿀 수 없습니다. 다른 사용자/관리자의 역할은 위에서 변경할 수 있습니다.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호 (옵션)</label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
              value={form.password || ''}
              onChange={e => setForm({ ...form, password: e.target.value })}
              placeholder="변경하지 않으려면 비워두세요"
              disabled={saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">비밀번호 확인</label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
              value={passwordConfirm}
              onChange={e => setPasswordConfirm(e.target.value)}
              placeholder="새 비밀번호를 다시 입력하세요"
              disabled={saving}
            />
          </div>
          {error && (
            <div className="mt-1 p-2 text-sm rounded border border-red-200 bg-red-50 text-red-700">
              {error}
            </div>
          )}
          <div className="pt-2 flex gap-3">
            <button type="button" onClick={onClose} disabled={saving} className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">취소</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-900">{saving ? '저장 중...' : '저장'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}


